import { useCallback, useEffect, useRef, useState } from 'react';
import { billingApi } from '../../services/billingApi';
import type { ClaimSummaryDto, PracticeClaimsFilters, WorkerStatus } from '../../types/billing';

const POLL_INTERVAL_MS = 5000;
const POST_ACTION_WINDOW_MS = 60_000;

export interface UsePracticeClaimsResult {
  items: ClaimSummaryDto[];
  workerStatus: WorkerStatus | null;
  nextCursor: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  reload: () => void;
  loadMore: () => Promise<void>;
  markActionTaken: () => void;
}

export function usePracticeClaims(
  practiceId: string | undefined,
  filters: PracticeClaimsFilters,
): UsePracticeClaimsResult {
  const [items, setItems] = useState<ClaimSummaryDto[]>([]);
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const postActionUntilRef = useRef<number>(0);
  // Keep a ref to items so the polling tick can read the latest value without
  // being listed as an effect dependency (which would reset the interval on
  // every state update).
  const itemsRef = useRef<ClaimSummaryDto[]>(items);
  // Keep a ref to filtersKey so loadMore can detect mid-flight filter changes
  // without recreating the callback on every filter change.
  const filtersKeyRef = useRef<string>('');

  // Stable key so that identical filter objects don't re-trigger effects.
  const filtersKey = JSON.stringify({
    s: filters.status,
    u: filters.uploadId,
    f: filters.from,
    t: filters.to,
    d: filters.dir,
    l: filters.limit,
  });

  // Sync refs on every commit so callbacks (loadMore, polling tick) can read
  // the latest values without listing them as effect dependencies. Runs in an
  // effect to avoid writing to refs during render (React docs flag this; also
  // keeps the component pure under future concurrent rendering features).
  useEffect(() => {
    itemsRef.current = items;
    filtersKeyRef.current = filtersKey;
  });

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const markActionTaken = useCallback(() => {
    postActionUntilRef.current = Date.now() + POST_ACTION_WINDOW_MS;
  }, []);

  // Initial load - re-runs on practiceId, filter, or manual reload changes.
  useEffect(() => {
    if (!practiceId) {
      setItems([]);
      setWorkerStatus(null);
      setNextCursor(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setWorkerStatus(null);
    setIsLoading(true);
    setError(null);

    billingApi
      .listPracticeClaims(practiceId, { ...filters, cursor: undefined })
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setWorkerStatus(page.workerStatus ?? null);
        setNextCursor(page.nextCursor);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load claims');
        setItems([]);
        setWorkerStatus(null);
        setNextCursor(null);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [practiceId, filtersKey, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load next page and append.
  const loadMore = useCallback(async () => {
    if (!practiceId || nextCursor === null || isLoadingMore) return;
    const filtersAtCallTime = filtersKeyRef.current;
    setIsLoadingMore(true);
    try {
      const page = await billingApi.listPracticeClaims(practiceId, {
        ...filters,
        cursor: nextCursor,
      });
      if (filtersAtCallTime !== filtersKeyRef.current) return;  // filters changed mid-flight; discard
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      if (filtersAtCallTime !== filtersKeyRef.current) return;  // filters changed; don't surface stale error
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setIsLoadingMore(false);
    }
  }, [practiceId, nextCursor, isLoadingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling - fires every POLL_INTERVAL_MS when a DISPATCHED row is visible
  // or when we are inside the post-action observation window.
  useEffect(() => {
    if (!practiceId) return;

    const tick = () => {
      if (document.visibilityState !== 'visible') return;

      const hasDispatched = itemsRef.current.some((c) => c.status === 'DISPATCHED');
      const inWindow = Date.now() < postActionUntilRef.current;

      if (!hasDispatched && !inWindow) return;

      // Re-fetch first page silently and merge by id so that rows beyond
      // the first page are preserved.
      billingApi
        .listPracticeClaims(practiceId, { ...filters, cursor: undefined })
        .then((page) => {
          setWorkerStatus(page.workerStatus ?? null);
          setItems((prev) => {
            const byId = new Map(prev.map((c) => [c.id, c]));
            let changed = false;
            for (const fresh of page.items) {
              const old = byId.get(fresh.id);
              // status is what flips during dispatch; queuePosition counts
              // down 3 → 2 → 1 → 0 while status stays DISPATCHED, so we
              // need to merge on either change to keep the queue indicator
              // accurate.
              if (
                !old
                || old.status !== fresh.status
                || old.queuePosition !== fresh.queuePosition
              ) {
                byId.set(fresh.id, fresh);
                changed = true;
              }
            }
            return changed ? Array.from(byId.values()) : prev;
          });
        })
        .catch(() => {
          // Swallow - manual reload is the user's fallback.
        });
    };

    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [practiceId, filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    items,
    workerStatus,
    nextCursor,
    isLoading,
    isLoadingMore,
    error,
    reload,
    loadMore,
    markActionTaken,
  };
}
